import { useCallback, useEffect, useState } from 'react';
import type {
  AdminTicketCounts,
  AdminTicketDetail,
  AdminTicketListResponse,
} from '../../../domain/superadmin/types';
import type { TicketStatus } from '../../../domain/support/guards';
import { useAppContext } from '../../../contexts/AppContext';
import {
  fetchAdminTicket,
  fetchAdminTickets,
  replyAdminTicket,
  updateAdminTicket,
} from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

const PAGE_SIZE = 25;
const EMPTY_COUNTS: AdminTicketCounts = { open: 0, in_progress: 0, resolved: 0, closed: 0 };

export function useAdminTickets() {
  const [data, setData] = useState<AdminTicketListResponse | null>(null);
  const [detail, setDetail] = useState<AdminTicketDetail | null>(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const handleForbidden = useCallback(() => {
    setCurrentPage('dashboard');
    notify.warning('Acesso não autorizado à área de superadmin.');
  }, [setCurrentPage]);

  const handleError = useCallback(
    (e: unknown, fallback: string) => {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        handleForbidden();
        return true;
      }
      setError(e instanceof Error ? e.message : fallback);
      return false;
    },
    [handleForbidden]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminTickets({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        status: status || undefined,
      });
      setData(result);
    } catch (e) {
      handleError(e, 'Erro ao carregar tickets');
    } finally {
      setIsLoading(false);
    }
  }, [page, q, status, handleError]);

  const loadDetail = useCallback(
    async (id: string) => {
      setDetail(null);
      setIsDetailLoading(true);
      setError(null);
      try {
        const result = await fetchAdminTicket(id);
        setDetail(result);
      } catch (e) {
        handleError(e, 'Erro ao carregar ticket');
      } finally {
        setIsDetailLoading(false);
      }
    },
    [handleError]
  );

  const updateStatus = useCallback(
    async (id: string, nextStatus: TicketStatus) => {
      setIsSaving(true);
      setError(null);
      try {
        const updated = await updateAdminTicket(id, nextStatus);
        setDetail(updated);
        await load();
      } catch (e) {
        handleError(e, 'Erro ao atualizar status do ticket');
      } finally {
        setIsSaving(false);
      }
    },
    [load, handleError]
  );

  const reply = useCallback(
    async (ticketId: string, body: string) => {
      setIsSaving(true);
      setError(null);
      try {
        const updated = await replyAdminTicket(ticketId, body);
        setDetail(updated);
        await load();
      } catch (e) {
        handleError(e, 'Erro ao responder ticket');
      } finally {
        setIsSaving(false);
      }
    },
    [load, handleError]
  );

  useEffect(() => {
    void load();
  }, [load]);

  return {
    data,
    counts: data?.counts ?? EMPTY_COUNTS,
    detail,
    isLoading,
    isDetailLoading,
    isSaving,
    error,
    refetch: load,
    loadDetail,
    updateStatus,
    reply,
    page,
    setPage,
    q,
    setQ,
    status,
    setStatus,
    pageSize: PAGE_SIZE,
  };
}
